import { useState, useCallback } from 'react';
import {
  callOpenAI,
  callAnthropic,
  callCustomEndpoint,
  callOpenAIWithImages,
  callAnthropicWithImages,
  callOpenRouterWithImages,
  repairJsonWithLLM
} from '../../utils/llm-generator/apiClients';
import {
  buildUserPrompt
} from '../../utils/llm-generator/promptBuilder';
import {
  parseQuestionResponse
} from '../../utils/llm-generator/questionTransformers';
import {
  convertPdfToImages
} from '../../utils/llm-generator/fileParser';

/**
 * Custom Hook für LLM Fragen-Generierung
 * 
 * Verwaltet:
 * - Generation State (isGenerating, log)
 * - Generierte Fragen
 * - API Calls zu verschiedenen Providern
 */
export function useLLMGeneration(config) {
  const [isGenerating, setIsGenerating] = useState(false);
  const [generationLog, setGenerationLog] = useState('');
  const [generatedQuestions, setGeneratedQuestions] = useState([]);
  
  /**
   * Add to generation log
   */
  const addLog = useCallback((message) => {
    setGenerationLog(prev => prev + message);
  }, []);
  
  /**
   * Clear generation log
   */
  const clearLog = useCallback(() => {
    setGenerationLog('');
  }, []);
  
  /**
   * Make LLM API call based on provider
   */
  const callLLM = useCallback(async (systemPrompt, userPrompt) => {
    const {
      llmProvider,
      apiKey,
      model,
      baseUrl,
      temperature,
      maxTokens
    } = config;
    
    let response;
    switch (llmProvider) {
      case 'openai':
      case 'groq':
      case 'openrouter':
        response = await callOpenAI(systemPrompt, userPrompt, apiKey, model, baseUrl, temperature, maxTokens);
        break;
      case 'anthropic':
        response = await callAnthropic(systemPrompt, userPrompt, apiKey, model, baseUrl, temperature, maxTokens);
        break;
      case 'custom':
        response = await callCustomEndpoint(systemPrompt, userPrompt, baseUrl, apiKey, model, temperature, maxTokens);
        break;
      default:
        throw new Error(`Unbekannter Provider: ${llmProvider}`);
    }
    
    return response;
  }, [config]);
  
  /**
   * Parse LLM response to questions, with optional JSON repair
   */
  const parseResponse = useCallback(async (response, enableJsonRepair = true) => {
    const {
      llmProvider,
      apiKey,
      model,
      baseUrl,
      temperature
    } = config;
    
    try {
      return parseQuestionResponse(response);
    } catch (parseError) {
      if (enableJsonRepair) {
        addLog('JSON-Parsing fehlgeschlagen. Versuche Reparatur...\n');
        
        const repairedJson = await repairJsonWithLLM(response, {
          llmProvider,
          apiKey,
          model,
          baseUrl,
          temperature: 0.1
        });
        
        const questions = parseQuestionResponse(repairedJson);
        addLog('JSON erfolgreich repariert!\n');
        return questions;
      }
      throw parseError;
    }
  }, [config, addLog]);
  
  /**
   * Generate questions from LLM
   * 
   * @param {Object} options - Generation options
   * @param {Array} options.fileContents - File contents array
   * @param {number} options.numberOfQuestions - Number of questions to generate
   * @param {string} options.difficulty - Difficulty level
   * @param {boolean} options.includeCodeBlocks - Include code blocks
   * @param {string} options.multipleChoiceMode - Multiple choice mode
   * @param {string} options.userGuidance - User guidance text
   * @param {Array} options.topicTags - Topic tags
   * @param {string} options.preSelectedContent - Pre-selected content (for chunked workflow)
   * @param {boolean} options.appendToExisting - Append to existing questions
   * @returns {Promise<{success: boolean, questions: Array, error: string|null}>}
   */
  const generateQuestions = useCallback(async (options) => {
    const {
      fileContents = [],
      numberOfQuestions = 10,
      difficulty = 'medium',
      includeCodeBlocks = true,
      multipleChoiceMode = 'single',
      userGuidance = '',
      topicTags = [],
      preSelectedContent = null,
      appendToExisting = false
    } = options;
    
    const {
      llmProvider,
      apiKey,
      systemPrompt,
      enableJsonRepair
    } = config;
    
    // Check for API key
    if (!apiKey && llmProvider !== 'custom' && llmProvider !== 'clipboard') {
      return {
        success: false,
        questions: [],
        error: 'Bitte API-Key in den Einstellungen eingeben'
      };
    }
    
    setIsGenerating(true);
    setGenerationLog('Starte Generierung...\n');
    
    try {
      // Build prompt
      const userPrompt = buildUserPrompt({
        fileContents: preSelectedContent
          ? [{ name: 'Vorausgewählter Inhalt', content: preSelectedContent }]
          : fileContents,
        numberOfQuestions,
        difficulty,
        includeCodeBlocks,
        multipleChoiceMode,
        userGuidance,
        topicTags,
        existingQuestions: appendToExisting ? generatedQuestions : []
      });
      
      addLog('Sende Anfrage an LLM...\n');
      
      // Call LLM
      const response = await callLLM(systemPrompt, userPrompt);
      
      addLog('Antwort erhalten. Parse Fragen...\n');
      
      // Parse response
      const questions = await parseResponse(response, enableJsonRepair);
      
      if (!questions || questions.length === 0) {
        throw new Error('Keine gültigen Fragen in der Antwort gefunden');
      }
      
      // Update questions state
      if (appendToExisting && generatedQuestions.length > 0) {
        setGeneratedQuestions(prev => [...prev, ...questions]);
        addLog(`${questions.length} neue Fragen hinzugefügt! (Gesamt: ${generatedQuestions.length + questions.length})\n`);
      } else {
        setGeneratedQuestions(questions);
        addLog(`${questions.length} Fragen erfolgreich generiert!\n`);
      }
      
      setIsGenerating(false);
      return {
        success: true,
        questions,
        error: null
      };
      
    } catch (err) {
      const errorMsg = `Fehler bei der Generierung: ${err.message}`;
      addLog(`FEHLER: ${err.message}\n`);
      setIsGenerating(false);
      
      return {
        success: false,
        questions: [],
        error: errorMsg
      };
    }
  }, [config, generatedQuestions, callLLM, parseResponse, addLog]);
  
  /**
   * Build clipboard prompt (for clipboard provider)
   */
  const buildClipboardPrompt = useCallback((options) => {
    const {
      fileContents = [],
      numberOfQuestions = 10,
      difficulty = 'medium',
      includeCodeBlocks = true,
      multipleChoiceMode = 'single',
      userGuidance = '',
      topicTags = [],
      preSelectedContent = null
    } = options;
    
    const { systemPrompt } = config;
    
    const userPrompt = buildUserPrompt({
      fileContents: preSelectedContent
        ? [{ name: 'Vorausgewählter Inhalt', content: preSelectedContent }]
        : fileContents,
      numberOfQuestions,
      difficulty,
      includeCodeBlocks,
      multipleChoiceMode,
      userGuidance,
      topicTags
    });
    
    return {
      system: systemPrompt,
      user: userPrompt
    };
  }, [config]);
  
  /**
   * Clear all generated questions
   */
  const clearQuestions = useCallback(() => {
    setGeneratedQuestions([]);
    setGenerationLog('');
  }, []);
  
  /**
   * Delete a question by index
   */
  const deleteQuestion = useCallback((index) => {
    setGeneratedQuestions(prev => prev.filter((_, i) => i !== index));
  }, []);
  
  /**
   * Update a specific question
   */
  const updateQuestion = useCallback((index, updatedQuestion) => {
    setGeneratedQuestions(prev => {
      const updated = [...prev];
      updated[index] = updatedQuestion;
      return updated;
    });
  }, []);
  
  /**
   * Add a question to the list
   */
  const addQuestion = useCallback((question) => {
    setGeneratedQuestions(prev => [...prev, question]);
  }, []);
  
  return {
    // State
    isGenerating,
    generationLog,
    generatedQuestions,
    
    // Setters
    setGeneratedQuestions,
    setIsGenerating,
    
    // Generation Actions
    generateQuestions,
    buildClipboardPrompt,
    
    // Question Management
    clearQuestions,
    deleteQuestion,
    updateQuestion,
    addQuestion,
    
    // Log Management
    addLog,
    clearLog,
    
    // Utilities
    callLLM,
    parseResponse,
    questionCount: generatedQuestions.length,
    hasQuestions: generatedQuestions.length > 0
  };
}

export default useLLMGeneration;
